import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "raffle-page grid h-full w-full grid-rows-[auto,minmax(0,1fr),auto] justify-items-center overflow-hidden px-6 py-8 text-jm_white" }
const _hoisted_2 = {
  key: 0,
  class: "raffle-live-card raffle-live-card--top absolute left-1/2 top-[calc(env(safe-area-inset-top,0px)+0.15rem)] z-20 w-[min(calc(100%-2rem),16.5rem)] -translate-x-1/2 overflow-hidden rounded-2xl border border-jm_white/25 bg-jm_dark_green/92 backdrop-blur-md"
}
const _hoisted_3 = { class: "raffle-live-content px-3.5 py-2.5 text-center" }
const _hoisted_4 = { class: "flex items-center justify-center gap-2" }
const _hoisted_5 = { class: "text-[0.62rem] font-bold uppercase tracking-[0.22em] text-jm_white/85" }
const _hoisted_6 = { class: "mt-1.5 text-[0.76rem] font-semibold leading-4 text-jm_white" }
const _hoisted_7 = { class: "mt-2 rounded-xl bg-white px-3 py-2 text-jm_green" }
const _hoisted_8 = { class: "text-[0.56rem] font-bold uppercase tracking-[0.16em] text-jm_green/65" }
const _hoisted_9 = { class: "mt-0.5 text-[0.92rem] font-bold uppercase leading-4" }
const _hoisted_10 = {
  key: 0,
  class: "mt-1 text-[0.68rem] font-semibold uppercase leading-3 text-jm_green/75"
}
const _hoisted_11 = {
  key: 1,
  class: "mt-1 text-[0.68rem] font-semibold uppercase leading-3 text-jm_green/75"
}
const _hoisted_12 = { class: "mt-2.5 flex items-center justify-center gap-2.5" }
const _hoisted_13 = { class: "raffle-live-code rounded-xl bg-black/25 px-3 py-1.5" }
const _hoisted_14 = { class: "text-[0.58rem] uppercase tracking-[0.18em] text-jm_white/70" }
const _hoisted_15 = { class: "mt-0.5 text-[1.05rem] font-bold tracking-[0.18em] text-jm_white" }
const _hoisted_16 = { class: "text-[0.58rem] uppercase tracking-[0.1em] text-jm_white/80" }
const _hoisted_17 = { class: "flex flex-col items-center" }
const _hoisted_18 = ["src"]
const _hoisted_19 = { class: "text-center text-[2rem] font-bold uppercase leading-none" }
const _hoisted_20 = { class: "mt-3 max-w-[18rem] text-center text-[1rem] leading-6" }
const _hoisted_21 = { class: "raffle-stage flex min-h-0 w-full max-w-[22rem] flex-col items-center justify-center" }
const _hoisted_22 = { class: "raffle-wheel relative flex items-center justify-center" }
const _hoisted_23 = ["src"]
const _hoisted_24 = { class: "absolute inset-6 flex flex-col items-center justify-center rounded-full border-2 border-jm_white/30 bg-white text-center text-jm_green shadow-2xl" }
const _hoisted_25 = { class: "mt-3 max-w-[12rem] text-sm uppercase leading-5 text-jm_green/80" }
const _hoisted_26 = {
  key: 0,
  class: "mt-4 max-w-[18rem] text-center text-sm leading-5 text-jm_orange"
}
const _hoisted_27 = { class: "raffle-actions relative flex w-full max-w-[22rem] flex-col gap-3 pb-4" }
const _hoisted_28 = ["disabled"]

import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import DefaultLayout from "../layouts/DefaultLayout.vue";
import logo from "@/assets/images/misc/logo_jaegermeister_white.svg";
import { useGlobalStore } from "@/stores/global";
import { useGameTracking } from "@/composables/useGameTracking";
import { claimRaffleResult } from "@/services/raffle";
import i18n from "@/plugins/i18n";
import {
  getPimRaffleConfig,
  type PimRaffleEntry,
} from "@/services/pimRaffleConfig";


export default /*@__PURE__*/_defineComponent({
  __name: 'raffleMove',
  setup(__props) {

const pimUnavailableFallbackEntries: PimRaffleEntry[] = [
  {
    type: "lose",
    title: "Nincs nyeremény",
    subtitle: "Legközelebb több szerencsét!",
  },
];

const globalStore = useGlobalStore();
const { kpiTracker, session } = useGameTracking();

const currentIndex = ref(0);
const isSpinning = ref(false);
const hasStopped = ref(false);
const isResolvingResult = ref(false);
const raffleError = ref("");
const raffleEntries = ref<PimRaffleEntry[]>([]);
const prizeProbability = ref(0.1);
const targetEntry = ref<PimRaffleEntry | null>(null);
const liveVerificationCode = ref("---");
const liveTimestamp = ref("--:--:--");
const campaignPrizeInformation = ref("");
let spinInterval: ReturnType<typeof setInterval> | null = null;
let liveWatermarkInterval: ReturnType<typeof setInterval> | null = null;

const visibleEntries = computed(() => raffleEntries.value);

const idleEntry = computed<PimRaffleEntry>(() => ({
  title: i18n.global.t("raffle.idle_title"),
  subtitle: i18n.global.t("raffle.idle_subtitle"),
  type: "lose",
}));

const currentEntry = computed(
  () =>
    (!isSpinning.value && !hasStopped.value
      ? idleEntry.value
      : visibleEntries.value[currentIndex.value]) || idleEntry.value
);
const showLiveWatermark = computed(
  () => hasStopped.value && globalStore.raffleResult === "win"
);

const clearSpinInterval = () => {
  if (spinInterval) {
    clearInterval(spinInterval);
    spinInterval = null;
  }
};

const clearLiveWatermarkInterval = () => {
  if (liveWatermarkInterval) {
    clearInterval(liveWatermarkInterval);
    liveWatermarkInterval = null;
  }
};

const buildLiveVerificationCode = () => {
  const sessionIdentifier = session.fingerprint.value || "guest";
  const timeBucket = Math.floor(Date.now() / 4000);
  const source = `${sessionIdentifier}-${timeBucket}`;
  let hash = 0;

  for (let index = 0; index < source.length; index += 1) {
    hash = (hash * 31 + source.charCodeAt(index)) | 0;
  }

  return String((Math.abs(hash) % 9000) + 1000);
};

const updateLiveWatermark = () => {
  liveVerificationCode.value = buildLiveVerificationCode();
  liveTimestamp.value = new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const resolveRaffleResult = async () => {
  const gameId = process.env.VUE_APP_GAME_ID || "";
  const identifier = session.fingerprint.value || "";

  if (!gameId || !identifier) {
    throw new Error("Missing game or session information for the raffle.");
  }

  const response = await claimRaffleResult(gameId, identifier);
  globalStore.setRaffleResult(response.result);
  campaignPrizeInformation.value =
    response.result === "win" ? response.campaign_information?.trim() || "" : "";
  return response.result;
};

const ensureRaffleConfig = async () => {
  try {
    const config = await getPimRaffleConfig();
    raffleEntries.value = config.prizes;
    prizeProbability.value = config.probability;
  } catch (error) {
    console.error("[RaffleMove] Error while loading PIM raffle config:", error);
    raffleEntries.value = pimUnavailableFallbackEntries;
    prizeProbability.value = 0;
  }
};

const randomEntry = (entries: PimRaffleEntry[]) =>
  entries[Math.floor(Math.random() * entries.length)];

const resolveTargetEntry = async () => {
  await ensureRaffleConfig();

  const prizeEntries = raffleEntries.value.filter(
    (entry) => entry.type === "prize"
  );
  const loseEntry =
    raffleEntries.value.find((entry) => entry.type === "lose");

  if (!loseEntry) {
    throw new Error("Missing lose entry in raffle configuration.");
  }

  const isWinCandidate =
    prizeEntries.length > 0 && Math.random() < prizeProbability.value;

  if (!isWinCandidate) {
    globalStore.setRaffleResult("lose");
    return loseEntry;
  }

  const claimResult = await resolveRaffleResult();

  if (claimResult === "win" && prizeEntries.length > 0) {
    return randomEntry(prizeEntries);
  }

  globalStore.setRaffleResult("lose");
  return loseEntry;
};

const startRaffle = async () => {
  raffleError.value = "";
  isResolvingResult.value = true;

  try {
    targetEntry.value = await resolveTargetEntry();
    currentIndex.value = Math.floor(Math.random() * visibleEntries.value.length);
    isSpinning.value = true;
    spinInterval = setInterval(() => {
      currentIndex.value = Math.floor(Math.random() * visibleEntries.value.length);
    }, 130);
  } catch (error) {
    console.error("[RaffleMove] Error while claiming raffle result:", error);
    raffleError.value =
      error instanceof Error
        ? error.message
        : "The raffle result could not be loaded.";
  } finally {
    isResolvingResult.value = false;
  }
};

const stopRaffle = () => {
  clearSpinInterval();
  if (targetEntry.value) {
    const targetIndex = visibleEntries.value.findIndex(
      (entry) =>
        entry.type === targetEntry.value?.type &&
        entry.title === targetEntry.value?.title &&
        entry.subtitle === targetEntry.value?.subtitle
    );

    if (targetIndex >= 0) {
      currentIndex.value = targetIndex;
    }
  }
  isSpinning.value = false;
  hasStopped.value = true;
};

const toggleRaffle = async () => {
  if (isResolvingResult.value) {
    return;
  }

  globalStore.playTapSound();

  if (isSpinning.value) {
    stopRaffle();
    return;
  }

  await startRaffle();
};

const finishRaffle = () => {
  globalStore.playTapSound();
  globalStore.changeStep(
    globalStore.shouldShowDailyLimitScreen ? "thatsEnough" : "ageCheck"
  );
  globalStore.setShouldShowDailyLimitScreen(false);
  globalStore.setCapturedPhotoFile(null);
  globalStore.setCapturedPhotoDataUrl(null);
  globalStore.setUploadedPhotoItemId(null);
  globalStore.setRaffleResult(null);
  campaignPrizeInformation.value = "";
  targetEntry.value = null;
  kpiTracker.resetGame();
  kpiTracker.resetTrackingData();
  globalStore.resetGame(kpiTracker.selectedPerson.value);
};

onMounted(() => {
  updateLiveWatermark();
  liveWatermarkInterval = setInterval(updateLiveWatermark, 1000);
});

onBeforeUnmount(() => {
  clearSpinInterval();
  clearLiveWatermarkInterval();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(DefaultLayout, { "background-image": "green" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (showLiveWatermark.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _cache[1] || (_cache[1] = _createElementVNode("div", { class: "raffle-live-marquee" }, [
                _createElementVNode("span", null, "LIVE • JÄGERMEISTER • LIVE • JÄGERMEISTER • LIVE"),
                _createElementVNode("span", null, "LIVE • JÄGERMEISTER • LIVE • JÄGERMEISTER • LIVE")
              ], -1)),
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _cache[0] || (_cache[0] = _createElementVNode("div", { class: "raffle-live-pulse" }, [
                    _createElementVNode("span"),
                    _createElementVNode("span"),
                    _createElementVNode("span")
                  ], -1)),
                  _createElementVNode("div", _hoisted_5, _toDisplayString(_ctx.$t("raffle.live_badge")), 1)
                ]),
                _createElementVNode("div", _hoisted_6, _toDisplayString(_ctx.$t("raffle.live_instruction")), 1),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("div", _hoisted_8, _toDisplayString(_ctx.$t("raffle.prize_label")), 1),
                  _createElementVNode("div", _hoisted_9, _toDisplayString(currentEntry.value.title), 1),
                  (currentEntry.value.subtitle)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(currentEntry.value.subtitle), 1))
                    : _createCommentVNode("", true),
                  (campaignPrizeInformation.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(campaignPrizeInformation.value), 1))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("div", _hoisted_13, [
                    _createElementVNode("div", _hoisted_14, _toDisplayString(_ctx.$t("raffle.live_code_label")), 1),
                    _createElementVNode("div", _hoisted_15, _toDisplayString(liveVerificationCode.value), 1)
                  ]),
                  _createElementVNode("div", _hoisted_16, _toDisplayString(liveTimestamp.value), 1)
                ])
              ])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("img", {
            src: _unref(logo),
            alt: "logo",
            class: "mb-6 h-20"
          }, null, 8, _hoisted_18),
          _createElementVNode("div", _hoisted_19, _toDisplayString(_ctx.$t("raffle.title")), 1),
          _createElementVNode("div", _hoisted_20, _toDisplayString(_ctx.$t("raffle.description")), 1)
        ]),
        _createElementVNode("div", _hoisted_21, [
          _createElementVNode("div", _hoisted_22, [
            _createElementVNode("div", {
              class: _normalizeClass(["absolute inset-0 animate-spin", { 'animate-none': !isSpinning.value }])
            }, [
              _createElementVNode("img", {
                src: require('@/assets/images/misc/circular.svg'),
                class: "h-full w-full object-cover scale-110"
              }, null, 8, _hoisted_23)
            ], 2),
            _createElementVNode("div", _hoisted_24, [
              _createElementVNode("div", {
                class: _normalizeClass(["px-6 text-[1.4rem] font-bold uppercase leading-tight", currentEntry.value.type === 'prize' ? 'text-jm_orange' : 'text-jm_green'])
              }, _toDisplayString(currentEntry.value.title), 3),
              _createElementVNode("div", _hoisted_25, _toDisplayString(currentEntry.value.subtitle), 1)
            ])
          ]),
          (raffleError.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_26, _toDisplayString(raffleError.value), 1))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_27, [
          (!hasStopped.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                class: _normalizeClass(["min-h-12 rounded-xl bg-jm_orange px-4 text-sm font-bold uppercase tracking-[0.15em] text-white", { 'cursor-not-allowed opacity-50': isResolvingResult.value }]),
                disabled: isResolvingResult.value,
                onClick: toggleRaffle
              }, _toDisplayString(isSpinning.value
              ? _ctx.$t("raffle.stop_button")
              : isResolvingResult.value
                ? _ctx.$t("raffle.loading_button")
                : _ctx.$t("raffle.start_button")), 11, _hoisted_28))
            : (_openBlock(), _createElementBlock("button", {
                key: 1,
                class: "min-h-12 rounded-xl bg-jm_orange px-4 text-sm font-bold uppercase tracking-[0.15em] text-white",
                onClick: finishRaffle
              }, _toDisplayString(_ctx.$t("raffle.finish_button")), 1))
        ])
      ])
    ]),
    _: 1
  }))
}
}

})